import { isBefore, startOfDay } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import { PayInvoiceCardResponse } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { Stack, Text } from '@eneco/sparky';
import { Attention, CircleCheckmark } from '@eneco/sparky/illustrations';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering } from '@sitecore/types/DashboardOverview';

import { StyledDashboardBlock } from './DashboardBlock';
import { DashboardLink } from './DashboardLink';

const PAYMENT_METHOD_PRIORITY: Record<string, number> = {
  BankTransfer: 3,
  DirectDebitExcludingYearlyBill: 2,
  DirectDebit: 1,
};

const amountPriority = (amount: number) => (amount > 0 ? 0 : amount < 0 ? 1 : 2);

type PayInvoiceCardFields = DashboardOverviewRendering['fields']['payInvoiceCard'];

const resolvePayInvoiceCardState = ({
  records,
  openAmount,
  amountOfBillingAccounts,
  fields,
  format,
  currency,
}: {
  records: PayInvoiceCardResponse['details']['summary']['records'];
  openAmount: number;
  amountOfBillingAccounts: number;
  amountOfOpenInvoices: number;
  hasUnpaidYearlyBill: boolean;
  amountOfUnpaidYearlyBills: number;
  fields: PayInvoiceCardFields;
  format: ReturnType<typeof useFormatter>['format'];
  currency: ReturnType<typeof useFormatter>['currency'];
}): { message: string; icon: React.ReactNode } => {
  if (openAmount < 0) {
    return {
      message: format(fields.reimbursementText.value, {
        amount: currency.euro(Math.abs(openAmount)),
        iban: records[0].iban,
      }),
      icon: <CircleCheckmark color="feedbackSuccess" size="large" />,
    };
  }

  const uniquePaymentMethods = new Set(records.map(r => r.paymentMethod));
  let paymentMethod = '';

  if (uniquePaymentMethods.size === 1) {
    paymentMethod = records[0].paymentMethod;
  } else if (uniquePaymentMethods.size > 1) {
    const representativeRecord = [...records].sort((a, b) => {
      const amountDiff = amountPriority(a.amount) - amountPriority(b.amount);
      if (amountDiff !== 0) return amountDiff;
      return (PAYMENT_METHOD_PRIORITY[b.paymentMethod] ?? 0) - (PAYMENT_METHOD_PRIORITY[a.paymentMethod] ?? 0);
    })[0];

    paymentMethod = representativeRecord.paymentMethod;
  }

  const showOpenAmountAndPayAllLink = amountOfBillingAccounts > 1;

  const buildCard = (message: string) => ({
    message: format(message, { amount: currency.euro(openAmount) }),
    showOpenAmountAndPayAllButton: showOpenAmountAndPayAllLink,
    icon: <Attention color="feedbackError" size="large" />,
  });

  const isDue = (items: { dueDate?: string | null; amount?: number }[]) =>
    items.some(
      item =>
        item.dueDate &&
        isBefore(startOfDay(new Date(item.dueDate)), startOfDay(new Date())) &&
        item.amount &&
        item.amount > 0,
    );
  const hasOpen = (items: { amount?: number }[]) => items.some(item => item.amount && item.amount > 0);

  if (paymentMethod === 'BankTransfer') {
    const items = records.filter(r => r.paymentMethod === 'BankTransfer').flatMap(r => r.openAmountItems);

    if (isDue(items)) return buildCard(fields?.dueOpenAmountBankTransferText?.value);
    if (hasOpen(items)) return buildCard(fields?.openAmountBankTransferText?.value);
  }

  if (paymentMethod === 'DirectDebit') {
    const items = records.filter(r => r.paymentMethod === 'DirectDebit').flatMap(r => r.openAmountItems);
    if (isDue(items)) return buildCard(fields?.dueOpenAmountDirectDebitText?.value);
    if (hasOpen(items))
      return {
        ...buildCard(fields?.openAmountDirectDebitText?.value),
        icon: <CircleCheckmark color="feedbackSuccess" size="large" />,
      };
  }

  if (paymentMethod === 'DirectDebitExcludingYearlyBill') {
    const yearlyItems = records.flatMap(r => r.openAmountItems.filter(item => item.invoiceType === 'YearlyBill'));
    const ddItems = records.filter(r => r.paymentMethod === 'DirectDebit').flatMap(r => r.openAmountItems);
    if (isDue(yearlyItems)) return buildCard(fields?.dueOpenAmountBankTransferText?.value);
    if (hasOpen(yearlyItems)) return buildCard(fields?.openAmountBankTransferText?.value);
    if (isDue(ddItems)) return buildCard(fields?.dueOpenAmountDirectDebitText?.value);
    if (hasOpen(ddItems)) return buildCard(fields?.openAmountBankTransferText?.value);
  }

  return { message: '', icon: null };
};

export const PayInvoiceCard = ({ card }: { card: PayInvoiceCardResponse }) => {
  const { fields } = useContent<DashboardOverviewRendering>();
  const { records } = card.details.summary;
  const amountOfBillingAccounts = records?.length ?? 0;

  const amountOfOpenInvoices = records.reduce((acc, record) => acc + record.amountOfUnpaidInvoices, 0);
  const openAmount = records.reduce(
    (acc, record) =>
      record.paymentMethod === 'DirectDebitExcludingYearlyBill'
        ? acc +
          record.openAmountItems
            .filter(item => item.invoiceType === 'YearlyBill')
            .reduce((sum, item) => sum + item.amount, 0)
        : acc + record.amount,
    0,
  );

  const hasUnpaidYearlyBill = records.some(record =>
    record.openAmountItems.some(item => item.invoiceType === 'YearlyBill' && item.amount > 0),
  );

  const amountOfUnpaidYearlyBills = records.reduce(
    (acc, record) =>
      acc + record.openAmountItems.filter(item => item.invoiceType === 'YearlyBill' && item.amount > 0).length,
    0,
  );
  const { format, currency } = useFormatter();

  const { message, icon } = resolvePayInvoiceCardState({
    records,
    openAmount,
    amountOfBillingAccounts,
    amountOfOpenInvoices,
    hasUnpaidYearlyBill,
    amountOfUnpaidYearlyBills,
    fields: fields.payInvoiceCard,
    format,
    currency,
  });

  return (
    <StyledDashboardBlock>
      <Stack direction="row" gap="4" alignY="center">
        <Stack.Item shrink={false}>{icon}</Stack.Item>
        <Stack>
          <Text weight="bold">
            <RichText html={message} />
          </Text>
        </Stack>
      </Stack>
      <DashboardLink
        href={fields.payInvoiceCard.invoicesLink.value.href}
        emphasis="high"
        trackingComponentName="pay_invoice_invoices_link"
        trackingComponentType="dashboard_link">
        {fields.payInvoiceCard.invoicesLink.value.text}
      </DashboardLink>
    </StyledDashboardBlock>
  );
};
